import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { Icon } from '../../components/ui/icon';
import { toast } from '../../components/ui/toast';
import { api, errorMessage } from '../../lib/api';
import { useRealtime } from '../../lib/realtime';
import { useCan } from '../../lib/session';
import { qk, useSettingsSummary, type ChannelDetail, type ChannelEvent, type QrResponse } from './api';
import { ago, formatPhone, fullDate, shortTime } from './format';
import { RelinkButton } from './relink';
import { QrImage } from './ui';

const QR_PERIOD = 60; // OpenWA: a QR is valid for 60 seconds

const STATUS_NOTE: Record<string, string> = {
  ready: 'המספר מחובר',
  connecting: 'מתחבר ל-OpenWA',
  qr_required: 'ממתין לסריקת QR',
  disconnected: 'המכשיר איבד חיבור',
  failed: 'החיבור נכשל',
  restricted: 'וואטסאפ הגביל את המספר',
  created: 'המספר נוצר ועוד לא קושר',
};

const EVENT_NOTE: Record<string, string> = {
  'session.qr': 'נוצר קוד QR לסריקה',
  'admin.connect': 'התחלת חיבור מהאתר',
  'admin.restart': 'הפעלה מחדש מהאתר',
  'admin.logout': 'ניתוק המספר מהאתר',
  'admin.pairing_code': 'הופק קוד קישור',
  'admin.profile': 'עדכון הפרופיל בוואטסאפ',
  'admin.limits': 'שינוי הקצב והמכסה',
  'admin.created': 'המספר נוצר במערכת',
  'admin.create_failed': 'יצירת ה-session ב-OpenWA נכשלה',
  'admin.webhook_registered': 'ה-webhook נרשם ב-OpenWA',
  'webhook.rejected': 'אירוע webhook נדחה (חתימה לא תקינה)',
  'session.disconnected': 'OpenWA דיווח על ניתוק',
  'session.reconnect_loop': 'ניסיונות חיבור חוזרים',
  'session.authenticated': 'הסריקה אושרה',
};

const SOURCE_NOTE: Record<string, string> = {
  'admin.restart': 'אחרי הפעלה מחדש',
  'admin.connect': 'אחרי התחלת חיבור',
  'admin.logout': 'אחרי ניתוק',
  qr_watcher: 'בזמן ההמתנה לסריקה',
  poll: 'בבדיקת המצב התקופתית',
  webhook: 'דיווח מ-OpenWA',
};

export function describeEvent(e: ChannelEvent): { text: string; note: string; sub?: string } {
  const p = (e.payload ?? {}) as Record<string, unknown>;
  if (e.type === 'session.status') {
    const to = String(p.to ?? '');
    const raw = typeof p.rawStatus === 'string' && p.rawStatus ? p.rawStatus : to;
    const src = typeof p.source === 'string' ? SOURCE_NOTE[p.source] : undefined;
    const err = typeof p.lastError === 'string' && p.lastError ? p.lastError : undefined;
    return { text: raw, note: STATUS_NOTE[to] ?? to, sub: err ?? src };
  }
  if (e.type === 'session.restriction') {
    return { text: e.type, note: p.active ? 'וואטסאפ הגביל את המספר' : 'ההגבלה הוסרה' };
  }
  return { text: e.type, note: EVENT_NOTE[e.type] ?? '' };
}

export function TimelinePanel({ events, hint, loading }: { events: ChannelEvent[]; hint: string; loading?: boolean }) {
  return (
    <div className="panel">
      <div className="panel-head">
        <h2>אירועי חיבור</h2>
        <span className="hint">{hint}</span>
      </div>
      {loading ? (
        <div className="panel-body">
          <div className="skel" style={{ height: 120 }} />
        </div>
      ) : events.length ? (
        <ul className="timeline" style={{ paddingBottom: 10 }}>
          {events.map((e) => {
            const d = describeEvent(e);
            return (
              <li key={e.id}>
                <span className="t-time">{shortTime(e.at)}</span>
                <span className="t-dot" />
                <span className="t-body">
                  <span className="wa2-ev">{d.text}</span>
                  {d.note}
                  {d.sub ? <small>{d.sub}</small> : null}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="panel-body small muted">עוד אין אירועים.</div>
      )}
    </div>
  );
}

export function KeepCallout() {
  return (
    <div className="callout warn mb">
      <Icon name="phone" />
      <div>
        <b>לא מנתקים את המכשיר המקושר מהטלפון.</b> בטלפון של מספר הלקוחות, תחת "מכשירים מקושרים", מופיע "NIVISION OpenWA". ניתוק משם
        מפסיק את כל השליחות, וחיבור מחדש עלול להיתקע באימות passkey.
      </div>
    </div>
  );
}

export function QuotaSummary({ channel, link = true }: { channel: ChannelDetail; link?: boolean }) {
  const { sentToday, dailyCap, remaining } = channel.quota;
  const pct = dailyCap ? Math.min(100, Math.round((sentToday / dailyCap) * 100)) : 0;
  return (
    <div className="panel">
      <div className="panel-head">
        <h2>מכסה להיום</h2>
        {link ? (
          <div className="actions">
            <Link className="btn sm ghost" to="/whatsapp/$tab" params={{ tab: 'limits' }}>
              לפרטים על המכסה והקצב
            </Link>
          </div>
        ) : null}
      </div>
      <div className="panel-body">
        <div className="row between">
          <div className="wa2-quota-n">
            {sentToday} <small>מתוך {dailyCap} הודעות</small>
          </div>
          <span className="small muted">נשארו {remaining}. מתאפס בחצות.</span>
        </div>
        <div className="wa2-meter mt-s" role="meter" aria-valuemin={0} aria-valuemax={dailyCap} aria-valuenow={sentToday} aria-label="מכסה יומית">
          <i style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

function useInvalidateChannel(id: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.channel(id) });
    void qc.invalidateQueries({ queryKey: qk.channels });
    void qc.invalidateQueries({ queryKey: ['statusbar'] });
  };
}

export function useRestart(channel: ChannelDetail) {
  const invalidate = useInvalidateChannel(channel.id);
  return useMutation({
    mutationFn: () => api<{ status: string }>(`/api/channels/${channel.id}/restart`, { method: 'POST' }),
    onSuccess: (r) => {
      toast(r.status === 'ready' ? 'החיבור הופעל מחדש. המכשיר נשאר מקושר והמספר מחובר' : 'מפעילים מחדש את החיבור ל-OpenWA');
      invalidate();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
}

function useConnect(channel: ChannelDetail) {
  const qc = useQueryClient();
  const invalidate = useInvalidateChannel(channel.id);
  return useMutation({
    mutationFn: () => api<QrResponse>(`/api/channels/${channel.id}/connect`, { method: 'POST' }),
    onSuccess: (r) => {
      if (r.qr) qc.setQueryData<QrResponse>(qk.qr(channel.id), { status: r.status, qr: r.qr, expiresInSec: QR_PERIOD });
      invalidate();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
}

function useEngineLabel(channelId: string) {
  const q = useSettingsSummary(channelId);
  if (!q.data) return null;
  if (q.data.provider === 'fake') return 'ספק מדומה (בדיקות)';
  return q.data.openwa.engine ?? 'whatsapp-web.js';
}

// ================= ready =================
function ReadyView({ channel, eventsLoading }: { channel: ChannelDetail; eventsLoading: boolean }) {
  const can = useCan();
  const restart = useRestart(channel);
  const engine = useEngineLabel(channel.id);
  const restricted = channel.status === 'restricted';
  return (
    <>
      <KeepCallout />
      {restricted ? (
        <div className="callout danger mb">
          <Icon name="shield" />
          <div>
            <b>וואטסאפ הגביל את המספר.</b> השליחה עצורה עד שההגבלה מוסרת.{' '}
            <Link className="link" to="/whatsapp/$tab" params={{ tab: 'restrictions' }}>
              לפרטים
            </Link>
          </div>
        </div>
      ) : null}
      <div className="split-wide">
        <div className="stack-lg">
          <div className="wa2-status" role="status">
            <div className="wa2-ic">
              <Icon name="check" />
            </div>
            <div className="grow">
              <span className="wa2-live">
                <i />
                מחובר עכשיו
              </span>
              <h2>{channel.name} מחובר</h2>
              <div className="wa2-phone ltr" style={{ textAlign: 'right' }}>
                {formatPhone(channel.phone)}
              </div>
              <dl className="wa2-facts">
                <div>
                  <dt>שם בוואטסאפ</dt>
                  <dd>{channel.pushName ?? '-'}</dd>
                </div>
                <div>
                  <dt>מנוע</dt>
                  <dd>
                    {engine ? (
                      <>
                        <span className="ltr">{engine}</span>
                        {channel.provider === 'openwa' ? ' דרך OpenWA' : ''}
                      </>
                    ) : (
                      '-'
                    )}
                  </dd>
                </div>
                <div>
                  <dt>מחובר מאז</dt>
                  <dd className="num">{fullDate(channel.connectedAt)}</dd>
                </div>
                <div>
                  <dt>בדיקת מצב אחרונה</dt>
                  <dd>{ago(channel.lastSeenAt)}</dd>
                </div>
                <div>
                  <dt>בתור עכשיו</dt>
                  <dd>
                    <Link className="link" to="/messages">
                      {channel.queue} הודעות
                    </Link>
                  </dd>
                </div>
                <div>
                  <dt>קבוצות שהמספר חבר בהן</dt>
                  <dd>
                    <Link className="link" to="/groups">
                      {channel.groupsCount} קבוצות
                    </Link>
                  </dd>
                </div>
              </dl>
              {can('channels.connect') ? (
                <div className="row mt">
                  <button className="btn" disabled={restart.isPending} onClick={() => restart.mutate()}>
                    <Icon name="refresh" />
                    {restart.isPending ? 'מפעילים מחדש…' : 'הפעלה מחדש של החיבור'}
                  </button>
                  <span className="small muted">המכשיר נשאר מקושר. שימושי כשהודעות נתקעות.</span>
                  <RelinkButton channelId={channel.id} phone={channel.phone} />
                </div>
              ) : null}
            </div>
          </div>

          <QuotaSummary channel={channel} />

          <div className="panel">
            <div className="panel-head">
              <h2>מה עושים אם</h2>
            </div>
            <ul className="wa2-not">
              <li>
                <Icon name="clock" />
                <span>
                  <b>הודעות נתקעות בתור והמספר נראה מחובר</b>
                  <small>לוחצים "הפעלה מחדש של החיבור". המכשיר נשאר מקושר ואין צורך בסריקה.</small>
                </span>
              </li>
              <li>
                <Icon name="alert" />
                <span>
                  <b>המספר ממתין לפעולה בוואטסאפ ווב</b>
                  <small>
                    לפעמים וואטסאפ מציג חלון "מה חדש" שהמערכת לא מצליחה לסגור. פותחים את וואטסאפ ווב בדפדפן, מאשרים את החלון, ואז לוחצים
                    "הפעלה מחדש".
                  </small>
                </span>
              </li>
              <li>
                <Icon name="phone" />
                <span>
                  <b>המכשיר נותק מהטלפון</b>
                  <small>המסך הזה יעבור לבד לסריקת QR. סורקים רק מהטלפון של מספר הלקוחות.</small>
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="stack-lg">
          <TimelinePanel events={channel.events} loading={eventsLoading} hint="מה OpenWA דיווח על החיבור. המצב נבדק כל 60 שניות." />
          <div className="callout">
            <Icon name="settings" />
            <div>
              ניתוק המספר, המנוע וה-webhook נמצאים בלשונית{' '}
              <Link className="link" to="/whatsapp/$tab" params={{ tab: 'advanced' }}>
                מתקדם
              </Link>
              .
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ================= down =================
function DownView({ channel, eventsLoading }: { channel: ChannelDetail; eventsLoading: boolean }) {
  const can = useCan();
  const restart = useRestart(channel);
  const connect = useConnect(channel);
  const failed = channel.status === 'failed';
  return (
    <div className="split-wide">
      <div className="stack-lg">
        <div className="wa2-status down" role="alert">
          <div className="wa2-ic">
            <Icon name="alert" />
          </div>
          <div className="grow">
            <span className="wa2-live">
              <i />
              לא מחובר
            </span>
            <h2>
              {failed ? 'החיבור נכשל' : 'המספר מנותק'} מאז {shortTime(channel.statusChangedAt)}
            </h2>
            <p className="muted mt-s">
              OpenWA דיווח <span className="wa2-ev">{channel.providerStatus ?? channel.status}</span>.{' '}
              {failed ? 'כדאי לבדוק בלשונית "מתקדם" ש-OpenWA זמין.' : 'בדרך כלל זה אומר שהטלפון איבד חיבור לאינטרנט או נכבה.'}
            </p>
            {channel.lastError ? <p className="small muted mt-s">שגיאה אחרונה: {channel.lastError}</p> : null}
            {can('channels.connect') ? (
              <div className="row mt">
                <button className="btn primary lg" disabled={restart.isPending} onClick={() => restart.mutate()}>
                  <Icon name="refresh" />
                  {restart.isPending ? 'מפעילים מחדש…' : 'הפעלה מחדש של החיבור'}
                </button>
                <button className="btn" disabled={connect.isPending} onClick={() => connect.mutate()}>
                  אם זה לא עוזר: סריקת QR חדשה
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <h2>מה קורה עכשיו</h2>
          </div>
          <div className="panel-body">
            <ul className="checklist">
              <li>
                <span className="ic warn">
                  <Icon name="clock" />
                </span>
                <span className="grow">
                  <b>השליחות מושהות.</b> שום הודעה לא יוצאת עד שהמספר יחזור.
                </span>
              </li>
              <li>
                <span className="ic warn">
                  <Icon name="list" />
                </span>
                <span className="grow">
                  <b>{channel.queue} הודעות ממתינות בתור.</b> הן ייצאו לבד, בקצב הרגיל, ברגע שהמספר יתחבר.{' '}
                  <Link className="link" to="/messages">
                    לרשימה
                  </Link>
                </span>
              </li>
              <li>
                <span className="ic warn">
                  <Icon name="alert" />
                </span>
                <span className="grow">
                  <b>התראה שתתיישן מעבר ל-6 שעות לא תישלח.</b> היא תסומן כחסומה בלוג ותיכלל בסיכום היומי.
                </span>
              </li>
              <li>
                <span className="ic ok">
                  <Icon name="mail" />
                </span>
                <span className="grow">
                  <b>נשלחים מייל לצוות והתראה לדפדפנים הרשומים.</b> לא דרך המספר.{' '}
                  <Link className="link" to="/settings/notifications">
                    הגדרות ההתראות
                  </Link>
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="callout info">
          <Icon name="info" />
          <div>
            <b>לפני שסורקים QR:</b> בודקים שהטלפון דולק ומחובר לאינטרנט, ושב"מכשירים מקושרים" עדיין מופיע "NIVISION OpenWA". אם הוא שם,
            הפעלה מחדש כמעט תמיד מספיקה.
          </div>
        </div>
      </div>

      <div className="stack-lg">
        <TimelinePanel events={channel.events} loading={eventsLoading} hint="מה OpenWA דיווח על החיבור." />
      </div>
    </div>
  );
}

// ================= QR =================
function QrView({ channel }: { channel: ChannelDetail }) {
  const can = useCan();
  const qc = useQueryClient();
  const allowed = can('channels.connect');
  const connect = useConnect(channel);
  const notStarted = channel.status === 'created';
  const qr = useQuery({
    queryKey: qk.qr(channel.id),
    queryFn: () => api<QrResponse>(`/api/channels/${channel.id}/qr`),
    enabled: allowed && !notStarted,
    refetchInterval: 30_000,
  });
  const [left, setLeft] = useState(QR_PERIOD);
  const [fade, setFade] = useState(false);
  const [pairPhone, setPairPhone] = useState(channel.phone ? formatPhone(channel.phone) : '+972 ');
  const [pairCode, setPairCode] = useState<string | null>(null);
  const value = qr.data?.qr ?? null;

  // "new code now" / countdown expired: the server returns the live code or restarts a stalled session
  const refresh = useMutation({
    mutationFn: () => api<QrResponse>(`/api/channels/${channel.id}/qr/refresh`, { method: 'POST' }),
    onSuccess: (r) => {
      qc.setQueryData<QrResponse>(qk.qr(channel.id), r);
      setLeft(QR_PERIOD);
      if (!r.qr && r.status !== 'ready') toast('עדיין אין קוד מ-OpenWA. נסו שוב בעוד כמה שניות.', 'error');
      void qc.invalidateQueries({ queryKey: qk.channel(channel.id), exact: true });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });


  // the server watcher announces a new QR (id only — the code itself is never broadcast);
  // refetch it through GET /api/channels/:id/qr, which checks channels.connect
  const onQr = useCallback(
    (p: { channelId: string }) => {
      if (p.channelId !== channel.id || !allowed) return;
      void qc.invalidateQueries({ queryKey: qk.qr(channel.id) });
    },
    [qc, channel.id, allowed],
  );
  useRealtime('channel.qr', onQr);

  // a new code restarts the countdown
  useEffect(() => {
    if (!value) return;
    setFade(true);
    const t = setTimeout(() => setFade(false), 220);
    return () => clearTimeout(t);
  }, [value]);
  // every answer from the server (even the same, still-valid code) restarts the countdown
  useEffect(() => {
    if (qr.data?.qr) setLeft(QR_PERIOD);
  }, [qr.dataUpdatedAt, qr.data?.qr]);

  useEffect(() => {
    if (!value) return;
    const t = setInterval(() => setLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [value]);

  // the countdown ended: ask the server for a live code (it restarts the session if WhatsApp stopped rotating)
  const refreshMutate = refresh.mutate;
  const refreshing = refresh.isPending;
  useEffect(() => {
    if (left === 0 && value && !refreshing) refreshMutate();
  }, [left, value, refreshing, refreshMutate]);

  const pair = useMutation({
    mutationFn: () => api<{ code: string }>(`/api/channels/${channel.id}/pairing-code`, { body: { phone: pairPhone } }),
    onSuccess: (r) => setPairCode(r.code),
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  const phoneLabel = channel.phone ? formatPhone(channel.phone) : channel.name;

  return (
    <>
      <div className="callout warn mb">
        <Icon name="alert" />
        <div>
          <b>
            סורקים רק מהטלפון של מספר הלקוחות (<span className="ltr">{phoneLabel}</span>).
          </b>{' '}
          סריקה מטלפון אחר תחבר את המספר הלא נכון, וההודעות ללקוחות ייצאו ממנו.
        </div>
      </div>
      <div className="grid cols-2">
        <div className="panel">
          <div className="panel-head">
            <h2>סורקים את הקוד</h2>
            <span className="pill warn">{channel.status === 'connecting' ? 'מתחבר' : 'ממתין לסריקה'}</span>
          </div>
          <div className="wa2-qrwrap">
            {!allowed ? (
              <p className="small muted" style={{ padding: '20px 0' }}>
                רק מי שמורשה לחבר מספרים רואה את הקוד.
              </p>
            ) : notStarted ? (
              <div style={{ padding: '30px 0', textAlign: 'center' }}>
                <p className="small muted mb">המספר עוד לא התחיל חיבור. לוחצים כדי להציג קוד לסריקה.</p>
                <button className="btn primary lg" disabled={connect.isPending} onClick={() => connect.mutate()}>
                  <Icon name="phone" />
                  {connect.isPending ? 'מתחילים…' : 'התחלת חיבור והצגת QR'}
                </button>
              </div>
            ) : (
              <>
                <div className={`qr-box${fade ? ' fade' : ''}`} id="wa2-qr">
                  {value ? <QrImage value={value} /> : <div className="skel" style={{ width: '100%', height: '100%' }} aria-label="טוען קוד" />}
                </div>
                <div className="wa2-ring" aria-hidden="true">
                  <i style={{ width: `${value ? Math.round((left / QR_PERIOD) * 100) : 0}%` }} />
                </div>
                <div className="qr-timer" aria-live="polite">
                  {!value
                    ? qr.isError
                      ? 'לא הצלחנו לקבל קוד. לוחצים "קוד חדש עכשיו".'
                      : 'ממתינים לקוד מ-OpenWA…'
                    : left === QR_PERIOD
                      ? `נוצר קוד חדש. מתחדש בעוד ${left} שניות`
                      : `הקוד מתחדש בעוד ${left} שניות`}
                </div>
                <button
                  className="btn ghost sm mt-s"
                  disabled={refresh.isPending || connect.isPending}
                  onClick={() => refresh.mutate()}
                >
                  <Icon name="refresh" />
                  {refresh.isPending ? 'מבקשים קוד חדש…' : 'קוד חדש עכשיו'}
                </button>
                <p className="small muted mt-s">
                  חיברתם בטעות מספר אחר, או שהסריקה נדחית? <RelinkButton channelId={channel.id} phone={channel.phone} className="btn ghost sm" />
                </p>
              </>
            )}
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <h2>איך סורקים</h2>
            <span className="hint">לוקח פחות מדקה. הטלפון צריך להיות מחובר לאינטרנט.</span>
          </div>
          <ol className="wa2-path">
            <li>
              <span>
                <b>פותחים וואטסאפ בטלפון של מספר הלקוחות</b>
                <small>הטלפון שתמיד דולק</small>
              </span>
            </li>
            <li>
              <span>
                <b>נכנסים להגדרות</b>
                <small>באייפון: בפינה למטה. באנדרואיד: שלוש הנקודות למעלה</small>
              </span>
            </li>
            <li>
              <span>
                <b>בוחרים "מכשירים מקושרים"</b>
              </span>
            </li>
            <li>
              <span>
                <b>לוחצים "קישור מכשיר"</b>
                <small>אם הטלפון מבקש טביעת אצבע או קוד, מאשרים</small>
              </span>
            </li>
            <li>
              <span>
                <b>מכוונים את המצלמה לקוד שכאן</b>
                <small>המסך יתעדכן לבד כשהחיבור יצליח</small>
              </span>
            </li>
          </ol>
          {allowed && !notStarted ? (
            <details className="wa2-adv">
              <summary>
                <Icon name="chevron" />
                חיבור עם קוד במקום QR
              </summary>
              <div className="panel-body">
                <div className="callout warn">
                  <Icon name="alert" />
                  <div>
                    משתמשים בזה רק אם אין דרך לסרוק, למשל כשהמצלמה לא עובדת. במנוע שלנו, בקשת קוד עלולה לנתק מכשיר מקושר אחר של המספר. הקוד
                    תקף לכמה דקות, ואחרי כמה ניסיונות כושלים וואטסאפ חוסם קישור לזמן מה.
                  </div>
                </div>
                <div className="field mt">
                  <label htmlFor="wa2-pn">מספר הטלפון שמקשרים</label>
                  <input className="input ltr" id="wa2-pn" dir="ltr" value={pairPhone} onChange={(e) => setPairPhone(e.target.value)} />
                </div>
                <button className="btn mt" disabled={pair.isPending || pairPhone.replace(/\D/g, '').length < 9} onClick={() => pair.mutate()}>
                  {pair.isPending ? 'מפיקים…' : 'הפקת קוד קישור'}
                </button>
                {pairCode ? (
                  <div className="mt">
                    <div className="wa2-pair">{pairCode}</div>
                    <p className="small muted mt-s">בטלפון: מכשירים מקושרים, קישור מכשיר, "קישור עם מספר טלפון במקום", ומקלידים את הקוד.</p>
                  </div>
                ) : null}
              </div>
            </details>
          ) : null}
        </div>
      </div>
    </>
  );
}

export function ConnectionTab({ channel, eventsLoading }: { channel: ChannelDetail; eventsLoading: boolean }) {
  const s = channel.status;
  if (s === 'ready' || s === 'restricted') return <ReadyView channel={channel} eventsLoading={eventsLoading} />;
  if (s === 'disconnected' || s === 'failed') return <DownView channel={channel} eventsLoading={eventsLoading} />;
  return <QrView channel={channel} />;
}
